import type { Achievement, GameState } from '../types';
import { images } from '../assets/images';

// Use the stars.png pixel art icon for all achievements
const ACHIEVEMENT_STAR_ICON = images.stars;

export class AchievementSystem {
  private achievements: Achievement[] = [];
  private onUnlockCallback?: (achievement: Achievement) => void;

  constructor() {
    this.initializeAchievements();
  }

  private initializeAchievements(): void {
    this.achievements = [
      // === COMBAT ACHIEVEMENTS ===
      {
        id: 'first_click',
        name: 'First Contact',
        description: 'Click an alien for the first time',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.totalClicks >= 1,
      },
      {
        id: 'click_master',
        name: 'Click Master',
        description: 'Click 100 times',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.totalClicks >= 100,
      },
      {
        id: 'click_legend',
        name: 'Click Legend',
        description: 'Click 1,000 times',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.totalClicks >= 1000,
      },
      {
        id: 'click_god',
        name: 'Click God',
        description: 'Click 10,000 times',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.totalClicks >= 10000,
      },
      {
        id: 'click_transcendent',
        name: 'Transcendent Clicker',
        description: 'Click 50,000 times',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.totalClicks >= 50000,
      },
      {
        id: 'first_blood',
        name: 'First Blood',
        description: 'Destroy your first alien',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.aliensKilled >= 1,
      },
      {
        id: 'alien_hunter',
        name: 'Alien Hunter',
        description: 'Destroy 50 aliens',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.aliensKilled >= 50,
      },
      {
        id: 'alien_slayer',
        name: 'Alien Slayer',
        description: 'Destroy 250 aliens',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.aliensKilled >= 250,
      },
      {
        id: 'alien_destroyer',
        name: 'Alien Destroyer',
        description: 'Destroy 1,000 aliens',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.aliensKilled >= 1000,
      },
      {
        id: 'alien_annihilator',
        name: 'Alien Annihilator',
        description: 'Destroy 5,000 aliens',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.aliensKilled >= 5000,
      },
      {
        id: 'alien_apocalypse',
        name: 'Alien Apocalypse',
        description: 'Destroy 10,000 aliens',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.aliensKilled >= 10000,
      },
      {
        id: 'damage_dealer',
        name: 'Damage Dealer',
        description: 'Deal 10,000 total damage',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.totalDamage >= 10000,
      },
      {
        id: 'damage_master',
        name: 'Damage Master',
        description: 'Deal 100,000 total damage',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.totalDamage >= 100000,
      },
      {
        id: 'damage_overlord',
        name: 'Damage Overlord',
        description: 'Deal 1,000,000 total damage',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.totalDamage >= 1000000,
      },
      {
        id: 'damage_deity',
        name: 'Damage Deity',
        description: 'Deal 100,000,000 total damage',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.totalDamage >= 100000000,
      },
      {
        id: 'damage_universe',
        name: 'Universe Destroyer',
        description: 'Deal 1,000,000,000 total damage',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.totalDamage >= 1000000000,
      },
      {
        id: 'boss_slayer',
        name: 'Boss Slayer',
        description: 'Defeat your first boss',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.bossesKilled >= 1,
      },
      {
        id: 'boss_hunter',
        name: 'Boss Hunter',
        description: 'Defeat 10 bosses',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.bossesKilled >= 10,
      },
      {
        id: 'boss_master',
        name: 'Boss Master',
        description: 'Defeat 50 bosses',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.bossesKilled >= 50,
      },
      {
        id: 'boss_annihilator',
        name: 'Boss Annihilator',
        description: 'Defeat 100 bosses',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.bossesKilled >= 100,
      },
      {
        id: 'boss_obliterator',
        name: 'Boss Obliterator',
        description: 'Defeat 200 bosses',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.bossesKilled >= 200,
      },
      {
        id: 'boss_legend',
        name: 'Boss Legend',
        description: 'Defeat 500 bosses',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.bossesKilled >= 500,
      },
      {
        id: 'boss_god',
        name: 'Boss God',
        description: 'Defeat 1000 bosses',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.bossesKilled >= 1000,
      },
      {
        id: 'alien_extinction',
        name: 'Alien Extinction',
        description: 'Destroy 25,000 aliens',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.aliensKilled >= 25000,
      },
      {
        id: 'damage_omnipotent',
        name: 'Omnipotent Destroyer',
        description: 'Deal 10,000,000,000 total damage',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.totalDamage >= 10000000000,
      },

      // === CRITICAL HIT ACHIEVEMENTS ===
      {
        id: 'first_crit',
        name: 'Lucky Shot',
        description: 'Land your first critical hit',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.criticalHits >= 1,
      },
      {
        id: 'crit_hunter',
        name: 'Crit Hunter',
        description: 'Land 100 critical hits',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.criticalHits >= 100,
      },
      {
        id: 'crit_master',
        name: 'Crit Master',
        description: 'Land 1,000 critical hits',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.criticalHits >= 1000,
      },
      {
        id: 'crit_legend',
        name: 'Crit Legend',
        description: 'Land 10,000 critical hits',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'combat',
        unlocked: false,
        check: (state) => state.stats.criticalHits >= 10000,
      },

      // === PROGRESSION ACHIEVEMENTS ===
      {
        id: 'level_5',
        name: 'Novice',
        description: 'Reach level 5',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 5,
      },
      {
        id: 'level_10',
        name: 'Apprentice',
        description: 'Reach level 10',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 10,
      },
      {
        id: 'level_25',
        name: 'Journeyman',
        description: 'Reach level 25',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 25,
      },
      {
        id: 'level_50',
        name: 'Expert',
        description: 'Reach level 50',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 50,
      },
      {
        id: 'level_60',
        name: 'Veteran',
        description: 'Reach level 60',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 60,
      },
      {
        id: 'level_65',
        name: 'Elite',
        description: 'Reach level 65',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 65,
      },
      {
        id: 'level_70',
        name: 'Champion',
        description: 'Reach level 70',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 70,
      },
      {
        id: 'level_75',
        name: 'Master',
        description: 'Reach level 75',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 75,
      },
      {
        id: 'level_80',
        name: 'Grandmaster',
        description: 'Reach level 80',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 80,
      },
      {
        id: 'level_85',
        name: 'Titan',
        description: 'Reach level 85',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 85,
      },
      {
        id: 'level_90',
        name: 'Demigod',
        description: 'Reach level 90',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 90,
      },
      {
        id: 'level_95',
        name: 'Immortal',
        description: 'Reach level 95',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 95,
      },
      {
        id: 'level_100',
        name: 'Legendary',
        description: 'Reach level 100',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 100,
      },
      {
        id: 'level_150',
        name: 'Transcendent',
        description: 'Reach level 150',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 150,
      },
      {
        id: 'level_200',
        name: 'Ascended',
        description: 'Reach level 200',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 200,
      },
      {
        id: 'level_250',
        name: 'Cosmic Voyager',
        description: 'Reach level 250',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 250,
      },
      {
        id: 'level_300',
        name: 'Stellar Champion',
        description: 'Reach level 300',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 300,
      },
      {
        id: 'level_400',
        name: 'Nebula Master',
        description: 'Reach level 400',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 400,
      },
      {
        id: 'level_500',
        name: 'Galaxy Conqueror',
        description: 'Reach level 500',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 500,
      },
      {
        id: 'level_600',
        name: 'Supernova',
        description: 'Reach level 600',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 600,
      },
      {
        id: 'level_700',
        name: 'Black Hole',
        description: 'Reach level 700',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 700,
      },
      {
        id: 'level_750',
        name: 'Universal Force',
        description: 'Reach level 750',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 750,
      },
      {
        id: 'level_800',
        name: 'Reality Bender',
        description: 'Reach level 800',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 800,
      },
      {
        id: 'level_900',
        name: 'Dimension Breaker',
        description: 'Reach level 900',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 900,
      },
      {
        id: 'level_1000',
        name: 'COSMIC GOD',
        description: 'Reach level 1000 - You are limitless!',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.level >= 1000,
      },
      {
        id: 'level_1250',
        name: 'Beyond Infinity',
        description: 'Reach level 1250',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        hidden: true,
        check: (state) => state.level >= 1250,
      },
      {
        id: 'level_1500',
        name: 'Omnipotent',
        description: 'Reach level 1500',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        hidden: true,
        check: (state) => state.level >= 1500,
      },
      {
        id: 'first_ascension',
        name: 'First Ascension',
        description: 'Ascend for the first time',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.prestigeLevel >= 1,
      },

      // === COLLECTION ACHIEVEMENTS (Ships) ===
      {
        id: 'first_ship',
        name: 'Fleet Commander',
        description: 'Recruit your first additional ship',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.shipsCount >= 2,
      },
      {
        id: 'small_fleet',
        name: 'Small Fleet',
        description: 'Command 5 ships',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.shipsCount >= 5,
      },
      {
        id: 'armada',
        name: 'Armada',
        description: 'Command 10 ships',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.shipsCount >= 10,
      },
      {
        id: 'space_force',
        name: 'Space Force',
        description: 'Command 20 ships',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.shipsCount >= 20,
      },
      {
        id: 'galactic_empire',
        name: 'Galactic Empire',
        description: 'Command 30 ships',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.shipsCount >= 30,
      },
      {
        id: 'armada_supreme',
        name: 'Supreme Armada',
        description: 'Command 50 ships',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.shipsCount >= 50,
      },

      // === UPGRADE ACHIEVEMENTS ===
      {
        id: 'first_upgrade',
        name: 'Innovator',
        description: 'Purchase your first upgrade',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.stats.totalUpgrades >= 1,
      },
      {
        id: 'upgrade_enthusiast',
        name: 'Upgrade Enthusiast',
        description: 'Purchase 25 upgrades',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.stats.totalUpgrades >= 25,
      },
      {
        id: 'upgrade_master',
        name: 'Upgrade Master',
        description: 'Purchase 100 upgrades',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.stats.totalUpgrades >= 100,
      },
      {
        id: 'upgrade_legend',
        name: 'Upgrade Legend',
        description: 'Purchase 500 upgrades',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.stats.totalUpgrades >= 500,
      },
      {
        id: 'upgrade_deity',
        name: 'Upgrade Deity',
        description: 'Purchase 1,000 upgrades',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.stats.totalUpgrades >= 1000,
      },
      {
        id: 'first_subupgrade',
        name: 'Researcher',
        description: 'Unlock your first special technology',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.stats.totalSubUpgrades >= 1,
      },
      {
        id: 'tech_collector',
        name: 'Tech Collector',
        description: 'Unlock 5 special technologies',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.stats.totalSubUpgrades >= 5,
      },
      {
        id: 'tech_master',
        name: 'Tech Master',
        description: 'Unlock 10 special technologies',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.stats.totalSubUpgrades >= 10,
      },
      {
        id: 'tech_legend',
        name: 'Tech Legend',
        description: 'Unlock 20 special technologies',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.stats.totalSubUpgrades >= 20,
      },
      {
        id: 'tech_god',
        name: 'Technology God',
        description: 'Unlock all special technologies',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'collection',
        unlocked: false,
        check: (state) => state.stats.totalSubUpgrades >= 33,
      },

      // === WEALTH ACHIEVEMENTS ===
      {
        id: 'first_thousand',
        name: 'Starter Capital',
        description: 'Accumulate 1,000 points',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.points >= 1000,
      },
      {
        id: 'ten_thousand',
        name: 'Entrepreneur',
        description: 'Accumulate 10,000 points',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.points >= 10000,
      },
      {
        id: 'hundred_thousand',
        name: 'Wealthy',
        description: 'Accumulate 100,000 points',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.points >= 100000,
      },
      {
        id: 'million',
        name: 'Millionaire',
        description: 'Accumulate 1,000,000 points',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.points >= 1000000,
      },
      {
        id: 'ten_million',
        name: 'Tycoon',
        description: 'Accumulate 10,000,000 points',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.points >= 10000000,
      },
      {
        id: 'hundred_million',
        name: 'Magnate',
        description: 'Accumulate 100,000,000 points',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.points >= 100000000,
      },
      {
        id: 'billion',
        name: 'Billionaire',
        description: 'Accumulate 1,000,000,000 points',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.points >= 1000000000,
      },

      // === TIME-BASED ACHIEVEMENTS ===
      {
        id: 'five_minutes',
        name: 'Getting Started',
        description: 'Play for 5 minutes',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.stats.playTime >= 300,
      },
      {
        id: 'thirty_minutes',
        name: 'Committed',
        description: 'Play for 30 minutes',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.stats.playTime >= 1800,
      },
      {
        id: 'one_hour',
        name: 'Dedicated',
        description: 'Play for 1 hour',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.stats.playTime >= 3600,
      },
      {
        id: 'three_hours',
        name: 'Addicted',
        description: 'Play for 3 hours',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.stats.playTime >= 10800,
      },
      {
        id: 'ten_hours',
        name: 'Obsessed',
        description: 'Play for 10 hours',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.stats.playTime >= 36000,
      },
      {
        id: 'one_day',
        name: 'No Life',
        description: 'Play for 24 hours (total)',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'progression',
        unlocked: false,
        check: (state) => state.stats.playTime >= 86400,
      },

      // === MASTERY ACHIEVEMENTS (specific upgrade levels) ===
      {
        id: 'speed_demon',
        name: 'Speed Demon',
        description: 'Reach Attack Speed level 20',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.attackSpeedLevel >= 20,
      },
      {
        id: 'speed_god',
        name: 'Speed God',
        description: 'Reach Attack Speed level 50',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.attackSpeedLevel >= 50,
      },
      {
        id: 'damage_king',
        name: 'Damage King',
        description: 'Reach Damage Amplifier level 20',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.pointMultiplierLevel >= 20,
      },
      {
        id: 'damage_emperor',
        name: 'Damage Emperor',
        description: 'Reach Damage Amplifier level 50',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.pointMultiplierLevel >= 50,
      },
      {
        id: 'crit_specialist',
        name: 'Crit Specialist',
        description: 'Reach Critical Strike level 20',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.critChanceLevel >= 20,
      },
      {
        id: 'passive_master',
        name: 'Passive Master',
        description: 'Reach Passive Income level 30',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.resourceGenLevel >= 30,
      },
      {
        id: 'scholar',
        name: 'Scholar',
        description: 'Reach Knowledge Core level 25',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.xpBoostLevel >= 25,
      },
      {
        id: 'balanced',
        name: 'Balanced',
        description: 'Have all main upgrades at level 10 or higher',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) =>
          state.shipsCount >= 10 &&
          state.attackSpeedLevel >= 10 &&
          state.pointMultiplierLevel >= 10 &&
          state.critChanceLevel >= 10 &&
          state.resourceGenLevel >= 10 &&
          state.xpBoostLevel >= 10,
      },
      {
        id: 'well_rounded',
        name: 'Well Rounded',
        description: 'Have all main upgrades at level 25 or higher',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) =>
          state.shipsCount >= 25 &&
          state.attackSpeedLevel >= 25 &&
          state.pointMultiplierLevel >= 25 &&
          state.critChanceLevel >= 25 &&
          state.resourceGenLevel >= 25 &&
          state.xpBoostLevel >= 25,
      },
      {
        id: 'ultimate_power',
        name: 'Ultimate Power',
        description: 'Have all main upgrades at level 50 or higher',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) =>
          state.shipsCount >= 50 &&
          state.attackSpeedLevel >= 50 &&
          state.pointMultiplierLevel >= 50 &&
          state.critChanceLevel >= 50 &&
          state.resourceGenLevel >= 50 &&
          state.xpBoostLevel >= 50,
      },
      {
        id: 'mega_fleet',
        name: 'Mega Fleet Commander',
        description: 'Command 75 ships',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.shipsCount >= 75,
      },
      {
        id: 'ultra_speed',
        name: 'Ultra Speed',
        description: 'Reach Attack Speed level 75',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.attackSpeedLevel >= 75,
      },
      {
        id: 'damage_cosmic',
        name: 'Cosmic Damage',
        description: 'Reach Damage Amplifier level 75',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.pointMultiplierLevel >= 75,
      },
      {
        id: 'crit_god',
        name: 'Critical God',
        description: 'Reach Critical Strike level 40',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.critChanceLevel >= 40,
      },
      {
        id: 'passive_overlord',
        name: 'Passive Overlord',
        description: 'Reach Passive Income level 60',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.resourceGenLevel >= 60,
      },
      {
        id: 'knowledge_deity',
        name: 'Knowledge Deity',
        description: 'Reach Knowledge Core level 50',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.xpBoostLevel >= 50,
      },
      {
        id: 'super_rich',
        name: 'Super Rich',
        description: 'Accumulate 10,000,000,000 points',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.points >= 10000000000,
      },
      {
        id: 'ultra_rich',
        name: 'Ultra Rich',
        description: 'Accumulate 100,000,000,000 points',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.points >= 100000000000,
      },
      {
        id: 'click_maniac',
        name: 'Click Maniac',
        description: 'Click 100,000 times',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.stats.totalClicks >= 100000,
      },
      {
        id: 'crit_storm',
        name: 'Critical Storm',
        description: 'Land 50,000 critical hits',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'mastery',
        unlocked: false,
        check: (state) => state.stats.criticalHits >= 50000,
      },

      // === SECRET ACHIEVEMENTS ===
      {
        id: 'secret_patience',
        name: 'Patience is a Virtue',
        description: 'Wait without clicking for 30 seconds',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        hidden: true,
        check: (state) =>
          state.stats.totalClicks === 0 && state.stats.playTime >= 30,
      },
      {
        id: 'secret_speed',
        name: 'Speed Clicker',
        description: 'Click 100 times in the first minute',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        hidden: true,
        check: (state) =>
          state.stats.totalClicks >= 100 && state.stats.playTime <= 60,
      },
      {
        id: 'secret_cosmic',
        name: 'Cosmic Ascension',
        description: 'Unlock the Cosmic Ascension Protocol',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['cosmic_ascension'] === true,
      },
      {
        id: 'secret_singularity',
        name: 'Singularity',
        description: 'Unlock the Singularity Power Core',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['singularity_core'] === true,
      },
      {
        id: 'secret_meaning',
        name: 'The Meaning of Life',
        description: 'Unlock the Meaning of Life upgrade',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['meaning_of_life'] === true,
      },
      {
        id: 'secret_perfect',
        name: 'Perfection',
        description: 'Unlock all special technologies',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        hidden: true,
        check: (state) => state.stats.totalSubUpgrades >= 33,
      },
      {
        id: 'secret_coffee',
        name: 'Caffeine Addict',
        description: 'Unlock the Crew Coffee Machine',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['coffee_machine'] === true,
      },
      {
        id: 'secret_answer',
        name: '42',
        description: 'Unlock the Answer to Everything',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['answer_to_everything'] === true,
      },
      {
        id: 'secret_dragon',
        name: 'Dragon Tamer',
        description: 'Unlock the Dragon Egg',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['dragon_egg'] === true,
      },
      {
        id: 'secret_gauntlet',
        name: 'Perfectly Balanced',
        description: 'Unlock the Infinity Gauntlet (Replica)',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['infinity_gauntlet'] === true,
      },
      {
        id: 'secret_plasma',
        name: 'Plasma Master',
        description: 'Unlock the Plasma Energy Matrix',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['plasma_matrix'] === true,
      },
      {
        id: 'secret_temporal',
        name: 'Time Lord',
        description: 'Unlock Temporal Acceleration Field',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['temporal_acceleration'] === true,
      },
      {
        id: 'secret_quantum',
        name: 'Quantum Physicist',
        description: 'Unlock Quantum Entanglement Core',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['quantum_entanglement'] === true,
      },
      {
        id: 'secret_dark_matter',
        name: 'Dark Matter Harvester',
        description: 'Unlock Dark Matter Engine',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['dark_matter_engine'] === true,
      },
      {
        id: 'secret_hyper',
        name: 'Hyper-Dimensional',
        description: 'Unlock Hyper-Dimensional Reactor',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['hyper_reactor'] === true,
      },
      {
        id: 'secret_reality',
        name: 'Reality Bender',
        description: 'Unlock Reality Anchor',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['reality_anchor'] === true,
      },
      {
        id: 'secret_nanobots',
        name: 'Nanotech Engineer',
        description: 'Unlock Self-Replicating Nanobots',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['nanobots'] === true,
      },
      {
        id: 'secret_hologram',
        name: 'Master of Illusion',
        description: 'Unlock Holographic Decoy System',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['holographic_decoys'] === true,
      },
      {
        id: 'secret_photon',
        name: 'Light Speed Master',
        description: 'Unlock Photon Wave Amplifier',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['photon_amplifier'] === true,
      },
      {
        id: 'secret_stellar',
        name: 'Stellar Engineer',
        description: 'Unlock Stellar Forge',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['stellar_forge'] === true,
      },
      {
        id: 'secret_nebula',
        name: 'Nebula Harvester',
        description: 'Unlock Nebula Energy Harvester',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['nebula_harvester'] === true,
      },
      {
        id: 'secret_battery',
        name: 'Cosmic Energy Master',
        description: 'Unlock Cosmic Energy Battery',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        check: (state) => state.subUpgrades['cosmic_battery'] === true,
      },
      {
        id: 'secret_tech_master_v2',
        name: 'Technology Master V2',
        description: 'Unlock 40 special technologies',
        icon: ACHIEVEMENT_STAR_ICON,
        category: 'secret',
        unlocked: false,
        hidden: true,
        check: (state) => state.stats.totalSubUpgrades >= 40,
      },
    ];
  }

  setOnUnlock(callback: (achievement: Achievement) => void): void {
    this.onUnlockCallback = callback;
  }

  checkAchievements(state: GameState): void {
    for (const achievement of this.achievements) {
      if (!achievement.unlocked && achievement.check(state)) {
        achievement.unlocked = true;
        state.achievements[achievement.id] = true;
        if (this.onUnlockCallback) {
          this.onUnlockCallback(achievement);
        }
      }
    }
  }

  updateFromState(state: GameState): void {
    for (const achievement of this.achievements) {
      achievement.unlocked = state.achievements[achievement.id] ?? false;
    }
  }

  getAchievements(): Achievement[] {
    return this.achievements;
  }

  getUnlockedCount(): number {
    return this.achievements.filter((a) => a.unlocked).length;
  }

  getTotalCount(): number {
    return this.achievements.length;
  }

  getProgress(): number {
    return (this.getUnlockedCount() / this.getTotalCount()) * 100;
  }
}
